import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  personalBelongings: [],
  currentBelonging: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all belongings (optionally by patient ID)
export const fetchPersonalBelongings = createAsyncThunk(
  'personalBelongings/fetchAll',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/personal-belongings/${patientId}`
        : '/personal-belongings';

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to fetch personal belongings'
      );
    }
  }
);

// Fetch a single belonging
export const fetchPersonalBelonging = createAsyncThunk(
  'personalBelongings/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/personal-belongings/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch personal belonging'
      );
    }
  }
);

// Create belonging
export const createPersonalBelonging = createAsyncThunk(
  'personalBelongings/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post(`/personal-belongings`, data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to create personal belonging'
      );
    }
  }
);

// Update belonging
export const updatePersonalBelonging = createAsyncThunk(
  'personalBelongings/update',
  async ({ case_id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/personal-belongings/case/${case_id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to update personal belonging'
      );
    }
  }
);

// Delete belonging
export const deletePersonalBelonging = createAsyncThunk(
  'personalBelongings/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/personal-belongings/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          'Failed to delete personal belonging'
      );
    }
  }
);

// ==================== Slice ====================
const personalBelongingsSlice = createSlice({
  name: 'personalBelongings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentBelonging: (state, action) => {
      state.currentBelonging = action.payload;
    },
    clearCurrentBelonging: (state) => {
      state.currentBelonging = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchPersonalBelongings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPersonalBelongings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personalBelongings = action.payload;
      })
      .addCase(fetchPersonalBelongings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchPersonalBelonging.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPersonalBelonging.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBelonging = action.payload;
      })
      .addCase(fetchPersonalBelonging.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createPersonalBelonging.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPersonalBelonging.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personalBelongings.unshift(action.payload);
      })
      .addCase(createPersonalBelonging.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updatePersonalBelonging.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePersonalBelonging.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;
        const index = state.personalBelongings.findIndex(
          (b) => b.id === updated.id
        );
        if (index !== -1) {
          state.personalBelongings[index] = updated;
        }
        if (state.currentBelonging?.id === updated.id) {
          state.currentBelonging = updated;
        }
      })
      .addCase(updatePersonalBelonging.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deletePersonalBelonging.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePersonalBelonging.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personalBelongings = state.personalBelongings.filter(
          (b) => b.id !== action.payload
        );
        if (state.currentBelonging?.id === action.payload) {
          state.currentBelonging = null;
        }
      })
      .addCase(deletePersonalBelonging.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentBelonging,
  clearCurrentBelonging,
} = personalBelongingsSlice.actions;

export default personalBelongingsSlice.reducer;
